<template>
  <div>
    <div v-if="loading" class="d-flex">
      <v-col cols="12">
        <v-sheet v-if="loading" :color="'grey lighten-4'" class="pa-3">
          <v-skeleton-loader type="card-heading, table-row-divider"> </v-skeleton-loader>
        </v-sheet>
      </v-col>
    </div>

    <div v-if="!loading && !!requestDetails" class="d-flex header-card">
      <div class="col-2">
        <v-card elevation="3" class="fill-height" color="var(--app-light-secondary-color)">
          <div class="info-item-details text-center overflow-hidden">
            <div class="info-item-details-header">
              {{ local.caseRelatedRequest.reconsiderationFromNonCaseParty.tableHeader.caseCode }}
            </div>
            <div class="info-item-details-value text-h5 mt-3" :title="requestDetails.caseCode">
              {{ requestDetails.caseCode }}
            </div>
          </div>
        </v-card>
      </div>
      <div class="col-10">
        <v-card elevation="3">
          <div class="info-item-box pa-0 ma-0">
            <div
              v-for="(header, index) in headers"
              :key="header.value"
              class="info-item-details"
              :class="index == 0 ? ' info-item-box-b' : ''"
            >
              <div class="info-item-details-header">
                {{ header.text }}
              </div>
              <div class="font-info mt-3">
                {{
                  header.formatter
                    ? header.formatter(requestDetails[header.value])
                    : requestDetails[header.value]
                }}
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <div v-if="requestDetails.objectKey && !loading" class="d-flex header-card">
      <div class="col-2">
        <v-card elevation="2" outlined class="fill-height">
          <v-tabs v-model="tab" vertical hide-slider>
            <template v-for="tabButton in caseFileTabs">
              <v-tab
                :key="tabButton.href"
                :disabled="tabButton.isDisabled"
                :href="tabButton.href"
                class="tab-button"
                active-class="tab-active"
              >
                <v-icon left class="tab-button-icon">
                  {{ tabButton.icon }}
                </v-icon>
                <span class="mr-1"> {{ tabButton.title }}</span>
              </v-tab>
            </template>
          </v-tabs>
        </v-card>
      </div>
      <div class="col-10">
        <v-tabs-items v-model="tab">
          <v-card elevation="2">
            <v-tab-item value="tab-1">
              <incoming-request-decisions-tab
                :id="requestDetails.id"
                :is-pega="true"
                :court-id="requestDetails.courtId"
                :case-object-key="requestDetails.caseObjectKey"
              ></incoming-request-decisions-tab>
            </v-tab-item>

            <v-tab-item value="tab-2">
              <incoming-request-sessions-tab
                :id="requestDetails.sessionId"
                :case-object-key="requestDetails.caseObjectKey"
                :is-pega="true"
                :court-id="requestDetails.courtId"
                :incoming-request-type="requestDetails.requestType"
              ></incoming-request-sessions-tab>
            </v-tab-item>
          </v-card>
        </v-tabs-items>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import IncomingRequestDecisionsTab from './decisions-tab.vue';
import IncomingRequestSessionsTab from './sessions-tab.vue';
import { toHijriDate } from '@/helpers/utils';

const { mapActions } = createNamespacedHelpers('CasesModule');

export default {
  components: {
    IncomingRequestDecisionsTab,
    IncomingRequestSessionsTab,
  },
  data() {
    return {
      requestDetails: {},
      tab: null,
      headers: [
        {
          value: 'caseTypeName',
          text: this.local.home.tabCommon.caseType,
        },
        {
          value: 'caseDate',
          text: this.local.home.tabCommon.caseDate,
          formatter: toHijriDate,
        },
      ],
      loading: false,
      formModel: {},
    };
  },
  computed: {
    requestObjectKey() {
      return this.$route.params.requestObjectKey;
    },
    caseFileTabs() {
      const tabs = [
        {
          href: '#tab-1',
          icon: 'mdi-account-group',
          title: this.local.caseFile.tabHeaders.decisions,
          isEnabled: true,
        },
        {
          href: '#tab-2',
          icon: 'mdi-account-group',
          title: this.local.caseFile.tabHeaders.sessions,
          isEnabled: true,
        },
      ];
      return tabs.filter((t) => t.isEnabled);
    },
  },
  mounted() {
    this.loading = true;
    this.loadData();
  },
  methods: {
    toHijriDate,
    ...mapActions(['getIncomingRequestDetails']),
    loadData() {
      this.getIncomingRequestDetails({
        requestObjectKey: this.requestObjectKey,
      })
        .then((result) => {
          this.requestDetails = result.data;
        })
        .catch((err) => {
          this.toaster.error(err.message);
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-button {
  margin: 5px;
  margin-left: 0px;
  font: normal normal normal 16px/24px Almarai;
  justify-content: right;
  letter-spacing: 0px;
  color: #8e9090 !important;
  opacity: 1;

  .tab-button-icon {
    color: #8e9090 !important;
  }

  &:hover {
    color: var(--app-primary-color) !important;
    font-weight: bold;
    background: var(--app-light-secondary-color) 0% 0% no-repeat padding-box !important;

    .tab-button-icon {
      color: var(--app-primary-color) !important;
    }
  }
}

.tab-active {
  font-weight: bold;
  color: var(--app-lighter-primary-color) !important;
  border-left: 4px solid var(--app-lighter-primary-color);

  .tab-button-icon {
    color: var(--app-primary-color) !important;
  }
}

::v-deep .tab-details {
  box-shadow: 5px 5px 5px #00000014;
  border-radius: 22px;
  min-height: 534px;
}

.info-item-box {
  display: flex;
  justify-content: space-between;
  flex: 1;
  height: 100%;
}

.info-item-details {
  display: flex;
  justify-content: center;
  flex-direction: column;
  flex-wrap: wrap;
  flex: 1;
  color: var(--app-dark-primary-color);
  min-height: 140px;
}

.info-item-details-title {
  font-size: 14px;
}

.info-item-details-header {
  font-size: 16px;
}

.info-item-details-value {
  font-weight: bold;
  font-size: 16px;
}

::v-deep .text-c-green {
  color: var(--app-primary-color);
}

.header-card {
  background-color: rgba(228, 221, 221, 0.22);
}

.info-item-box-b {
  background: var(--app-light-secondary-color) 0% 0% no-repeat padding-box;
}

.font-info {
  font-size: 16px;
  font-weight: 900;
}
</style>
